/**
 * Customer Management Page
 * Admin dashboard for full customer lifecycle management
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  Chip,
  Button,
  IconButton,
  TextField,
  InputAdornment,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tab,
  Tabs,
  Avatar,
  LinearProgress,
  Tooltip,
  Badge,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  ListItemAvatar,
  useTheme,
  alpha,
  Skeleton,
  Alert,
} from '@mui/material';
import {
  Search,
  FilterList,
  Add,
  Business,
  Person,
  Email,
  Phone,
  TrendingUp,
  AttachMoney,
  CheckCircle,
  Warning,
  Schedule,
  Visibility,
  Edit,
  MoreVert,
  Assignment,
  Timeline,
  Message,
  Notifications,
  Refresh,
  Download,
  OpenInNew,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

// Types
interface Customer {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  phone: string | null;
  company_name: string;
  company_website: string | null;
  industry: string | null;
  company_size: string | null;
  tier: string;
  status: CustomerStatus;
  add_ons: string[];
  stripe_customer_id: string | null;
  stripe_subscription_id: string | null;
  subscription_status: string | null;
  monthly_value: number;
  total_paid: number;
  retainer_paid: boolean;
  registered_at: string | null;
  subscription_started_at: string | null;
  onboarding_started_at: string | null;
  onboarding_completed_at: string | null;
  last_activity_at: string | null;
  login_count: number;
  assigned_admin_id: string | null;
  assigned_admin_name: string | null;
  internal_notes: string | null;
  created_at: string;
  activity_count: number;
  pending_tasks: number;
}

type CustomerStatus = 
  | 'prospect'
  | 'pending_subscription'
  | 'pending_retainer'
  | 'onboarding'
  | 'active'
  | 'paused'
  | 'churned'
  | 'delinquent';

interface CustomerStats {
  statusBreakdown: Record<string, number>;
  tierBreakdown: Record<string, number>;
  totalMRR: number;
  newThisMonth: number;
  needingAttention: number;
  recentActivity: Activity[];
}

interface Activity {
  id: string;
  customer_id: string;
  action: string;
  category: string;
  description: string | null;
  company_name: string;
  first_name: string;
  last_name: string;
  created_at: string;
}

// Status configuration
const STATUS_CONFIG: Record<CustomerStatus, { label: string; color: 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning'; icon: React.ReactNode }> = {
  prospect: { label: 'Prospect', color: 'default', icon: <Person /> },
  pending_subscription: { label: 'Pending Subscription', color: 'warning', icon: <Schedule /> },
  pending_retainer: { label: 'Pending Retainer', color: 'info', icon: <AttachMoney /> },
  onboarding: { label: 'Onboarding', color: 'primary', icon: <Assignment /> },
  active: { label: 'Active', color: 'success', icon: <CheckCircle /> },
  paused: { label: 'Paused', color: 'warning', icon: <Schedule /> },
  churned: { label: 'Churned', color: 'error', icon: <Warning /> },
  delinquent: { label: 'Delinquent', color: 'error', icon: <Warning /> },
};

const TIER_LABELS: Record<string, string> = {
  starter: 'Core Site + Telephony',
  professional: 'Growth Stack',
  growth: 'Ops Suite',
  enterprise: 'Enterprise Transformation',
};

// Format currency
const formatCurrency = (amount: number) => {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);
};

// Format date
const formatDate = (date: string | null) => {
  if (!date) return '-';
  return new Date(date).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

// Relative time
const getRelativeTime = (date: string | null) => {
  if (!date) return 'Never';
  const now = new Date();
  const then = new Date(date);
  const diffMs = now.getTime() - then.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return formatDate(date);
};

const CustomersPage: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();

  // State
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [stats, setStats] = useState<CustomerStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [tierFilter, setTierFilter] = useState<string>('');

  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);

  // Tabs
  const [activeTab, setActiveTab] = useState(0);

  // Selected customer for detail view
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [detailOpen, setDetailOpen] = useState(false);

  // Fetch customers
  const fetchCustomers = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('redex_admin_token');
      
      const params = new URLSearchParams({
        page: String(page + 1),
        limit: String(rowsPerPage),
        ...(searchQuery && { search: searchQuery }),
        ...(statusFilter && { status: statusFilter }),
        ...(tierFilter && { tier: tierFilter }),
      });

      const response = await fetch(`/api/admin/customers?${params}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) throw new Error('Failed to fetch customers');

      const data = await response.json();
      if (data.success) {
        setCustomers(data.data.customers);
        setTotalCount(data.data.pagination.total);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch customers');
    } finally {
      setLoading(false);
    }
  };

  // Fetch stats
  const fetchStats = async () => {
    try {
      const token = localStorage.getItem('redex_admin_token');
      const response = await fetch('/api/admin/customers/stats', {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) throw new Error('Failed to fetch stats');

      const data = await response.json();
      if (data.success) {
        setStats(data.data);
      }
    } catch (err) {
      console.error('Failed to fetch stats:', err);
    }
  };

  useEffect(() => {
    fetchCustomers();
    fetchStats();
  }, [page, rowsPerPage, searchQuery, statusFilter, tierFilter]);

  // Handle customer click
  const handleCustomerClick = (customer: Customer) => {
    navigate(`/customers/${customer.id}`);
  };

  // Stat cards
  const renderStatCards = () => {
    if (!stats) {
      return (
        <Grid container spacing={3} sx={{ mb: 4 }}>
          {[1, 2, 3, 4].map((i) => (
            <Grid item xs={12} sm={6} md={3} key={i}>
              <Skeleton variant="rounded" height={120} />
            </Grid>
          ))}
        </Grid>
      );
    }

    const activeCount = (stats.statusBreakdown.active || 0) + (stats.statusBreakdown.onboarding || 0);
    const pipelineCount = (stats.statusBreakdown.prospect || 0) + 
      (stats.statusBreakdown.pending_subscription || 0) + 
      (stats.statusBreakdown.pending_retainer || 0);

    const statCards = [
      {
        title: 'Monthly Recurring Revenue',
        value: formatCurrency(stats.totalMRR),
        icon: <AttachMoney />,
        color: theme.palette.success.main,
        trend: '+12%',
      },
      {
        title: 'Active Customers',
        value: activeCount,
        icon: <CheckCircle />,
        color: theme.palette.primary.main,
        subtitle: `${stats.statusBreakdown.onboarding || 0} onboarding`,
      },
      {
        title: 'In Pipeline',
        value: pipelineCount,
        icon: <Timeline />,
        color: theme.palette.info.main,
        subtitle: 'Prospects & pending',
      },
      {
        title: 'Needs Attention',
        value: stats.needingAttention,
        icon: <Warning />,
        color: stats.needingAttention > 0 ? theme.palette.error.main : theme.palette.success.main,
        subtitle: 'Overdue or delinquent',
      },
    ];

    return (
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {statCards.map((stat, index) => (
          <Grid item xs={12} sm={6} md={3} key={index}>
            <Card
              sx={{
                height: '100%',
                background: `linear-gradient(135deg, ${alpha(stat.color, 0.1)} 0%, ${alpha(stat.color, 0.05)} 100%)`,
                border: `1px solid ${alpha(stat.color, 0.2)}`,
                transition: 'transform 0.2s, box-shadow 0.2s',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: `0 8px 24px ${alpha(stat.color, 0.2)}`,
                },
              }}
            >
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                  <Avatar
                    sx={{
                      bgcolor: alpha(stat.color, 0.15),
                      color: stat.color,
                      width: 48,
                      height: 48,
                    }}
                  >
                    {stat.icon}
                  </Avatar>
                  {stat.trend && (
                    <Chip
                      label={stat.trend}
                      size="small"
                      sx={{
                        bgcolor: alpha(theme.palette.success.main, 0.1),
                        color: theme.palette.success.main,
                        fontWeight: 600,
                      }}
                    />
                  )}
                </Box>
                <Typography variant="h4" fontWeight={700} sx={{ mb: 0.5 }}>
                  {stat.value}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {stat.title}
                </Typography>
                {stat.subtitle && (
                  <Typography variant="caption" color="text.secondary">
                    {stat.subtitle}
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    );
  };

  // Filters bar
  const renderFilters = () => (
    <Paper sx={{ p: 2, mb: 3 }}>
      <Grid container spacing={2} alignItems="center">
        <Grid item xs={12} md={4}>
          <TextField
            fullWidth
            size="small"
            placeholder="Search customers..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
            }}
          />
        </Grid>
        <Grid item xs={6} md={2}>
          <FormControl fullWidth size="small">
            <InputLabel>Status</InputLabel>
            <Select
              value={statusFilter}
              label="Status"
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <MenuItem value="">All Statuses</MenuItem>
              {Object.entries(STATUS_CONFIG).map(([key, config]) => (
                <MenuItem key={key} value={key}>
                  {config.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={6} md={2}>
          <FormControl fullWidth size="small">
            <InputLabel>Tier</InputLabel>
            <Select
              value={tierFilter}
              label="Tier"
              onChange={(e) => setTierFilter(e.target.value)}
            >
              <MenuItem value="">All Tiers</MenuItem>
              {Object.entries(TIER_LABELS).map(([key, label]) => (
                <MenuItem key={key} value={key}>
                  {label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} md={4} sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={() => {
              fetchCustomers();
              fetchStats();
            }}
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            startIcon={<Download />}
          >
            Export
          </Button>
        </Grid>
      </Grid>
    </Paper>
  );

  // Customer table
  const renderCustomerTable = () => (
    <TableContainer component={Paper}>
      {loading && <LinearProgress />}
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>Company</TableCell>
            <TableCell>Contact</TableCell>
            <TableCell>Tier</TableCell>
            <TableCell>Status</TableCell>
            <TableCell align="right">MRR</TableCell>
            <TableCell>Last Activity</TableCell>
            <TableCell align="center">Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {customers.map((customer) => {
            const statusConfig = STATUS_CONFIG[customer.status];
            return (
              <TableRow
                key={customer.id}
                hover
                sx={{ cursor: 'pointer' }}
                onClick={() => handleCustomerClick(customer)}
              >
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Avatar
                      sx={{
                        bgcolor: alpha(theme.palette.primary.main, 0.1),
                        color: theme.palette.primary.main,
                      }}
                    >
                      {customer.company_name.charAt(0)}
                    </Avatar>
                    <Box>
                      <Typography variant="subtitle2" fontWeight={600}>
                        {customer.company_name}
                      </Typography>
                      {customer.company_website && (
                        <Typography variant="caption" color="text.secondary">
                          {customer.company_website.replace(/^https?:\/\//, '')}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    {customer.first_name} {customer.last_name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {customer.email}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Chip
                    label={TIER_LABELS[customer.tier] || customer.tier}
                    size="small"
                    variant="outlined"
                  />
                </TableCell>
                <TableCell>
                  <Chip
                    label={statusConfig.label}
                    size="small"
                    color={statusConfig.color}
                    icon={statusConfig.icon as React.ReactElement}
                  />
                  {customer.pending_tasks > 0 && (
                    <Badge
                      badgeContent={customer.pending_tasks}
                      color="warning"
                      sx={{ ml: 1 }}
                    >
                      <Assignment fontSize="small" color="action" />
                    </Badge>
                  )}
                </TableCell>
                <TableCell align="right">
                  <Typography variant="subtitle2" fontWeight={600}>
                    {formatCurrency(customer.monthly_value)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    /month
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    {getRelativeTime(customer.last_activity_at)}
                  </Typography>
                </TableCell>
                <TableCell align="center">
                  <Tooltip title="View Details">
                    <IconButton size="small" onClick={(e) => {
                      e.stopPropagation();
                      handleCustomerClick(customer);
                    }}>
                      <Visibility />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Edit">
                    <IconButton size="small" onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/customers/${customer.id}/edit`);
                    }}>
                      <Edit />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            );
          })}
          {!loading && customers.length === 0 && (
            <TableRow>
              <TableCell colSpan={7} align="center" sx={{ py: 6 }}>
                <Typography color="text.secondary">
                  No customers found
                </Typography>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
      <TablePagination
        component="div"
        count={totalCount}
        page={page}
        onPageChange={(_, newPage) => setPage(newPage)}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={(e) => {
          setRowsPerPage(parseInt(e.target.value, 10));
          setPage(0);
        }}
        rowsPerPageOptions={[5, 10, 25, 50]}
      />
    </TableContainer>
  );

  // Recent activity sidebar
  const renderRecentActivity = () => (
    <Paper sx={{ p: 2, height: '100%' }}>
      <Typography variant="h6" fontWeight={600} gutterBottom>
        Recent Activity
      </Typography>
      <Divider sx={{ mb: 2 }} />
      <List dense>
        {stats?.recentActivity.slice(0, 8).map((activity) => (
          <ListItem key={activity.id} sx={{ px: 0 }}>
            <ListItemAvatar>
              <Avatar sx={{ width: 32, height: 32, bgcolor: alpha(theme.palette.primary.main, 0.1) }}>
                {activity.company_name?.charAt(0) || '?'}
              </Avatar>
            </ListItemAvatar>
            <ListItemText
              primary={
                <Typography variant="body2" noWrap>
                  <strong>{activity.company_name}</strong>
                </Typography>
              }
              secondary={
                <Typography variant="caption" color="text.secondary" noWrap>
                  {activity.description || activity.action} • {getRelativeTime(activity.created_at)}
                </Typography>
              }
            />
          </ListItem>
        ))}
        {(!stats?.recentActivity || stats.recentActivity.length === 0) && (
          <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
            No recent activity
          </Typography>
        )}
      </List>
    </Paper>
  );

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="h4" fontWeight={800} gutterBottom>
            Customer Management
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Track and manage customer lifecycle from prospect to active engagement
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => navigate('/customers/new')}
          sx={{
            background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
          }}
        >
          Add Customer
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Stats */}
      {renderStatCards()}

      {/* Main content */}
      <Grid container spacing={3}>
        <Grid item xs={12} lg={9}>
          {/* Filters */}
          {renderFilters()}

          {/* Table */}
          {renderCustomerTable()}
        </Grid>

        {/* Sidebar */}
        <Grid item xs={12} lg={3}>
          {renderRecentActivity()}
        </Grid>
      </Grid>
    </Container>
  );
};

export default CustomersPage;
